import React, { useEffect, useState, useRef } from "react";
import { X, Download, Maximize2, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { useMusicAutoplayControls, MusicNowPlaying } from "@/components/MusicLocation";

const SLIDE_MS = 4000;

/**
 * Full-screen image viewer used across the app (AssistantWidget, Feed, Ads).
 * Props:
 * - src (string): single image. Or `images` (string[]) + `startIndex` for a carousel.
 * - onClose (fn)
 * - filename (optional string)
 * - music (optional {preview_url,title,artist,cover}): plays continuously while open — across every photo —
 *   and auto-advances the carousel; stops only on close or manual pause.
 */
export default function FullscreenImageViewer({ src, images, startIndex = 0, onClose, filename, music }) {
  const list = Array.isArray(images) && images.length ? images : (src ? [src] : []);
  const open = list.length > 0;
  const [idx, setIdx] = useState(startIndex);
  const touchX = useRef(null);
  const { playing: musicPlaying, toggle: toggleMusic } = useMusicAutoplayControls(music, open);
  const listKey = list.join("|");

  useEffect(() => { setIdx(Math.min(Math.max(0, startIndex), Math.max(0, list.length - 1))); }, [startIndex, listKey, list.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % list.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + list.length) % list.length);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, list.length]);

  // Slideshow: while the music plays, step through every photo so the song covers the whole album.
  useEffect(() => {
    if (!open || list.length < 2 || !music?.preview_url || !musicPlaying) return;
    const ms = Math.max(1000, Number(music.slide_seconds || 0) * 1000) || SLIDE_MS;
    const iv = setInterval(() => setIdx((i) => (i + 1) % list.length), ms);
    return () => clearInterval(iv);
  }, [open, list.length, music?.preview_url, music?.slide_seconds, musicPlaying]);

  if (!open) return null;
  const current = list[idx] || list[0];
  const multi = list.length > 1;

  const doDownload = async () => {
    try {
      const r = await fetch(current, { credentials: "include" });
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `imagem-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(current, "_blank");
    }
  };

  const go = (d) => setIdx((i) => (i + d + list.length) % list.length);

  return (
    <div
      className="fixed inset-0 z-[140] bg-black/90 backdrop-blur-sm flex items-center justify-center animate-fade-in"
      data-testid="fullscreen-image-viewer"
      onClick={onClose}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current == null || !multi) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (dx <= -40) go(1); else if (dx >= 40) go(-1);
        touchX.current = null;
      }}
    >
      <img
        key={current}
        src={current}
        alt="Ampliada"
        className="max-w-[95vw] max-h-[90vh] object-contain rounded-md shadow-2xl animate-fade-in"
        onClick={(e) => e.stopPropagation()}
        data-testid="fullscreen-image-el"
      />
      {multi && (
        <>
          <button onClick={(e) => { e.stopPropagation(); go(-1); }} data-testid="fullscreen-image-prev" aria-label="Anterior"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center justify-center transition-colors">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); go(1); }} data-testid="fullscreen-image-next" aria-label="Próxima"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center justify-center transition-colors">
            <ChevronRight className="w-6 h-6" />
          </button>
          <div className="absolute top-5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md text-white text-xs font-medium" data-testid="fullscreen-image-counter">
            {idx + 1}/{list.length}
          </div>
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="fullscreen-image-dots">
            {list.map((u, i) => (
              <button key={`${u}-${i}`} type="button" aria-label={`Foto ${i + 1}`} onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "bg-white w-5" : "bg-white/50 w-1.5"}`} />
            ))}
          </div>
        </>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="fullscreen-image-close"
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center justify-center transition-colors"
        aria-label="Fechar"
      >
        <X className="w-5 h-5" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); doDownload(); }}
        data-testid="fullscreen-image-download"
        className="absolute top-4 left-4 h-11 px-4 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white flex items-center gap-2 transition-colors text-sm"
      >
        <Download className="w-4 h-4" /> Alta qualidade
      </button>
      {music?.preview_url && (
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <MusicNowPlaying music={music} playing={musicPlaying} testid="fullscreen-image-music" />
          <button type="button" onClick={toggleMusic} data-testid="fullscreen-image-music-toggle" aria-label={musicPlaying ? "Pausar música" : "Tocar música"}
            className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md text-white flex items-center justify-center transition-colors">
            {musicPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
          </button>
        </div>
      )}
      <span className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/70 text-xs flex items-center gap-1 whitespace-nowrap">
        <Maximize2 className="w-3 h-3" /> {multi ? "← → para navegar · " : ""}Toque fora ou ESC para fechar
      </span>
    </div>
  );
}
